import { useEffect, useSyncExternalStore } from 'react';
import { companyRequest } from '@/company/api';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { workspaceErrorCopy } from './workspace-errors';

// The Inbox (collaboration v1): GET /api/v1/company/inbox and
// POST /api/v1/company/inbox/read. Items carry the existing domain routes their
// buttons call; the Inbox never decides anything itself.

export interface InboxActor {
  userId: string;
  name: string | null;
}
export interface InboxAction {
  label: string;
  method: string;
  path: string;
  body: unknown;
}
export interface InboxItemRef {
  type: string;
  id: string;
  itemKind?: string;
  itemId?: string;
  /** A sharing request's action (share, publish_skill, revise_result, set_knowledge). */
  action?: string;
  /** An agent request (Poneva Everywhere): the grant that asked, the agent's name, the
   * permission family, and the exact connection and action the request is about. */
  grantId?: string;
  agentName?: string;
  family?: string;
  connectionId?: string;
}
export interface InboxItem {
  id: string;
  provider: string;
  kind: string;
  title: string;
  detail: string | null;
  preview: string | null;
  /** False when the preview had to be cut: the item then has no actions here
   * and is decided on its own page (href). */
  previewComplete: boolean;
  /** Where the actions are offered: on the list row only when the title is the
   * whole decision; otherwise in the detail pane beside the preview. */
  decideIn: 'row' | 'detail';
  actor: InboxActor | null;
  createdAt: string;
  actions: InboxAction[];
  href: string | null;
  ref: InboxItemRef | null;
}
/** What an agent did through a grant without waiting for the person (an Agent activity update). */
export interface InboxAgentActivity {
  outcome: 'allowed' | 'automatic' | 'refused';
  family?: string;
  connectionId?: string;
  action?: string;
  /** Why a refusal changed nothing: `connection_read` when the connection is set to Read. */
  reason?: string;
  /** The connection's name, when the refusal names one. */
  connectionName?: string;
  requestId?: string;
}
export interface InboxUpdate {
  /** Server-resolved source identity; presentation only, never permission. */
  sourceKey?: string | null;
  /** The agent that did it (an Agent activity update). */
  agent?: { grantId: string; name: string };
  activity?: InboxAgentActivity;
  id: string;
  kind: string;
  title: string;
  body: string | null;
  href: string | null;
  actor: InboxActor | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}
export interface Inbox {
  needsYou: InboxItem[];
  updates: InboxUpdate[];
  waiting: InboxItem[];
  counts: { needsYou: number; unreadUpdates: number };
}

export const INBOX_POLL_MS = 30_000;
const root = '/api/v1/company/inbox';

const count = (value: unknown, fallback: number) =>
  Number.isInteger(value) && (value as number) >= 0 ? (value as number) : fallback;
const list = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/** The Inbox as the page and badge read it. A malformed answer (an older
 * server, a proxy page) reads as an empty Inbox, never as a broken sidebar. */
export function inboxFrom(value: unknown): Inbox {
  const body = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const needsYou = list<InboxItem>(body.needsYou);
  const updates = list<InboxUpdate>(body.updates);
  const counts = (body.counts && typeof body.counts === 'object' ? body.counts : {}) as Record<
    string,
    unknown
  >;
  return {
    needsYou,
    updates,
    waiting: list<InboxItem>(body.waiting),
    counts: {
      needsYou: count(counts.needsYou, needsYou.length),
      unreadUpdates: count(counts.unreadUpdates, updates.filter((update) => !update.read).length),
    },
  };
}

export const inboxApi = {
  read: async () => inboxFrom(await companyRequest<unknown>(root)),
  markRead: (input: { ids: string[] } | { all: true }) =>
    companyRequest<{ read: number }>(`${root}/read`, 'POST', input),
  // An item's action is one of Chat's own company routes, exactly as the
  // server described it (inbox/service.mjs checks the same shape).
  act: (action: InboxAction, locale?: string) => {
    if (!action.path.startsWith('/api/v1/company/'))
      return Promise.reject(new Error(workspaceErrorCopy(locale).inboxActionUnavailable));
    return companyRequest(action.path, action.method, action.body ?? undefined);
  },
};

/** The sidebar badge: what needs the person plus unread updates. */
export const inboxBadge = (inbox: Inbox | null) =>
  inbox ? inbox.counts.needsYou + inbox.counts.unreadUpdates : 0;

/* One shared, polled copy per signed-in person and company: the sidebar badge
   and the Inbox page read the same answer, and only one poll runs while any of
   them is mounted and the page is visible. */
interface Snapshot {
  key: string | null;
  inbox: Inbox | null;
  loading: boolean;
  error: string;
}
let snapshot: Snapshot = { key: null, inbox: null, loading: false, error: '' };
const listeners = new Set<() => void>();
let generation = 0;
let subscribers = 0;
let pollingKey: string | null = null;
let pollingLocale: string | undefined;
let timer: ReturnType<typeof setTimeout> | null = null;

function publish(next: Snapshot) {
  snapshot = next;
  for (const listener of [...listeners]) listener();
}

export async function refreshInbox(key: string, locale?: string) {
  const asked = ++generation;
  const kept = snapshot.key === key ? snapshot.inbox : null;
  publish({ key, inbox: kept, loading: true, error: '' });
  try {
    const inbox = await inboxApi.read();
    if (asked === generation) publish({ key, inbox, loading: false, error: '' });
  } catch {
    if (asked === generation)
      publish({
        key,
        inbox: kept,
        loading: false,
        error: workspaceErrorCopy(locale).inboxUnavailable,
      });
  }
}

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

function schedule() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    if (pollingKey && visible()) void refreshInbox(pollingKey, pollingLocale);
    if (pollingKey) schedule();
  }, INBOX_POLL_MS);
}

function onVisible() {
  if (pollingKey && visible()) void refreshInbox(pollingKey, pollingLocale);
}

function startPolling(key: string, locale?: string) {
  subscribers += 1;
  if (pollingKey === key && subscribers > 1) return;
  pollingKey = key;
  pollingLocale = locale;
  if (subscribers === 1 && typeof document !== 'undefined')
    document.addEventListener('visibilitychange', onVisible);
  void refreshInbox(key, locale);
  schedule();
}

function stopPolling() {
  subscribers -= 1;
  if (subscribers > 0) return;
  subscribers = 0;
  pollingKey = null;
  if (timer) clearTimeout(timer);
  timer = null;
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The Inbox for `key` (person and company), polled every 30 s while visible. */
export function useInbox(key: string | null) {
  const locale = useChatInterfaceLocale();
  const current = useSyncExternalStore(subscribe, () => snapshot);
  useEffect(() => {
    if (!key) return;
    startPolling(key, locale);
    return stopPolling;
  }, [key, locale]);
  const mine = key !== null && current.key === key;
  return {
    inbox: mine ? current.inbox : null,
    loading: mine ? current.loading : Boolean(key),
    error: mine ? current.error : '',
    refresh: () => (key ? refreshInbox(key, locale) : Promise.resolve()),
  };
}

export function resetInboxForTests() {
  generation += 1;
  snapshot = { key: null, inbox: null, loading: false, error: '' };
  subscribers = 0;
  pollingKey = null;
  if (timer) clearTimeout(timer);
  timer = null;
}
